import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDelivery,
  createWriteoff,
  getDeliveries,
  getLastPrices,
  getLots,
  getStockAsOf,
  getPackagings,
  getProducts,
  getRecipes,
  getStockBalances,
  getShortages,
  getSuppliers,
  createSupplier,
  getUnits,
  getWarehouseGroups,
  getVats,
  createProduct,
  getWarehouses,
  getWriteoffs,
  createTransfer,
  updateTransfer,
  getSubstitutions,
  getDeletedMovements,
  restoreDeletedMovement,
  createStocktake,
  updateDelivery,
  deleteDelivery,
  updateWriteoff,
  updateStocktake,
  getInterchangeGroups,
  createInterchangeGroup,
  updateInterchangeGroup,
  deleteInterchangeGroup,
  getPendingReports,
  getReportPostingPreview,
  createReportPosting,
  getSoldProducts,
  getSoldDaily,
  getSoldRecipes,
  getInventorySettings,
  updateInventorySettings,
  isStockShortageError,
  type InventorySettings,
  type DeliveriesListParams,
  type CreateDeliveryPayload,
  type CreateWriteoffPayload,
  type CreateTransferPayload,
  type CreateStocktakePayload,
  type CreateReportPostingPayload,
  type SoldParams,
  type InterchangeGroupPayload,
  type SupplierInput,
  type ProductInput,
} from "../api/inventory";

// ─── Складови настройки (per ресторант) ────────────────────────────────────
export const useInventorySettings = () =>
  useQuery({
    queryKey: ["inventory", "settings"],
    queryFn: getInventorySettings,
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateInventorySettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InventorySettings) => updateInventorySettings(payload),
    onSuccess: (data) => {
      qc.setQueryData(["inventory", "settings"], data);
    },
  });
};

// ─── Lookups ──────────────────────────────────────────────────────────────
// `enabled` съществува заради одит лога: там справочниците трябват само когато
// отвореният запис е складов, а не при всяко зареждане на страницата. Останалите
// екрани не подават нищо и се държат както преди.
export const useWarehouses = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: getWarehouses,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useSuppliers = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "suppliers"],
    queryFn: getSuppliers,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useCreateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SupplierInput) => createSupplier(payload),
    // `return` — така `mutateAsync` изчаква презареждането на справочника и
    // новият доставчик вече е в списъка, когато го избираме автоматично.
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["inventory", "suppliers"] }),
  });
};

// Справочниците за модала „Нов продукт" — теглят се само когато той е отворен.
export const useUnits = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "units"],
    queryFn: getUnits,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useWarehouseGroups = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "warehouse-groups"],
    queryFn: getWarehouseGroups,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useVats = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "vats"],
    queryFn: getVats,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductInput) => createProduct(payload),
    // `return` — `mutateAsync` изчаква презареждането, за да е новият продукт
    // в списъка, когато редът му се добавя в документа.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory", "products"] }),
  });
};

// Всички продукти, вкл. меко изтритите (`deleted: true`) — за имена, мерки и
// ДДС в документите и справките. Търсачки, „Целият списък" и другите пикери
// минават през `pickableProducts` (components/product/deletedProducts.ts).
export const useProducts = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "products"],
    queryFn: getProducts,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const usePackagings = () =>
  useQuery({
    queryKey: ["inventory", "packagings"],
    queryFn: getPackagings,
    staleTime: 5 * 60 * 1000,
  });

// ─── Deliveries ───────────────────────────────────────────────────────────
// Без warehouseId → всички доставки от всички складове.
export const useDeliveries = (params: DeliveriesListParams | null) =>
  useQuery({
    queryKey: ["inventory", "deliveries", params],
    queryFn: () => getDeliveries(params as DeliveriesListParams),
    enabled: !!params,
  });

// Последните доставки на доставчик — за темплейт-селектора при нова доставка.
export const useSupplierDeliveries = (supplierId: number | null) =>
  useQuery({
    queryKey: ["inventory", "deliveries", "by-supplier", supplierId],
    queryFn: () => getDeliveries({ supplierId, limit: 20 }),
    enabled: supplierId != null,
    staleTime: 60 * 1000,
  });

export const useStockBalances = (
  warehouseId: number | null,
  asOf?: string | null, // ISO ден; без него → текущи, с него → наличности към датата
) =>
  useQuery({
    queryKey: ["inventory", "stock", warehouseId, asOf ?? null],
    queryFn: () => getStockBalances(warehouseId as number, asOf),
    enabled: !!warehouseId,
  });

// Недостиг за ВСИЧКИ складове (forward-only) + разбивка по отчети.
export const useShortages = () =>
  useQuery({
    queryKey: ["inventory", "shortages"],
    queryFn: () => getShortages(),
  });

export const useLastPrices = (
  warehouseId: number | null,
  supplierId?: number | null,
) =>
  useQuery({
    queryKey: ["inventory", "last-prices", warehouseId, supplierId ?? null],
    queryFn: () => getLastPrices(warehouseId as number, supplierId),
    enabled: !!warehouseId,
    staleTime: 60 * 1000,
  });

export const useRecipes = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "recipes"],
    queryFn: getRecipes,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useLots = (warehouseId: number | null, productId: number | null) =>
  useQuery({
    queryKey: ["inventory", "lots", warehouseId, productId],
    queryFn: () => getLots(warehouseId as number, productId as number),
    enabled: !!warehouseId && !!productId,
  });

// Наличност + партиди на продукт КЪМ края на деня (`asOfDay` — YYYY-MM-DD).
// Ключът е същият като в изписването — един кеш, една инвалидация.
export const useStockAsOf = (warehouseId: number | null, productId: number | null, asOfDay: string | null) =>
  useQuery({
    queryKey: ["inventory", "stock-as-of", warehouseId, productId, asOfDay, null],
    queryFn: () => getStockAsOf(warehouseId as number, productId as number, asOfDay as string),
    enabled: !!warehouseId && !!productId && !!asOfDay,
  });

// ─── Изписвания ───────────────────────────────────────────────────────────
export const useWriteoffs = (params: DeliveriesListParams | null) =>
  useQuery({
    queryKey: ["inventory", "writeoffs", params],
    queryFn: () => getWriteoffs(params as DeliveriesListParams),
    enabled: !!params,
  });

export const useCreateWriteoff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWriteoffPayload) => createWriteoff(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "writeoffs"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock"] });
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock-history"] });
    },
    onError: (error) => refreshStockAfterShortage(qc, error),
  });
};

// BE отказа записа за наличност, а листът я смяташе за стигаща (колега е
// изписал междувременно, данните още се зареждаха или рецептата е сменена):
// наличността към датата и съставките се теглят наново, за да покажат
// маркерите по редовете ТОЧНО кое не стига.
export const refreshStockAfterShortage = (
  qc: ReturnType<typeof useQueryClient>,
  error: unknown,
) => {
  if (!isStockShortageError(error)) return;
  qc.invalidateQueries({ queryKey: ["inventory", "stock-as-of"] });
  qc.invalidateQueries({ queryKey: ["inventory", "recipe-ingredients"] });
};

// ─── Прехвърляне между складове (transfer) ──────────────────────────────────
// Списъкът и справката — `useInventoryTransfers.ts` (складът е по избор).
export const useCreateTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransferPayload) => createTransfer(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock"] });
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock-history"] });
    },
  });
};

export const useUpdateTransfer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateTransferPayload }) =>
      updateTransfer(id, payload),
    onSuccess: () => invalidateAll(qc),
  });
};

// ─── Инвентаризации ───────────────────────────────────────────────────────
// Справката и последните по склад — `useInventoryStocktakes.ts`.
export const useCreateStocktake = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStocktakePayload) => createStocktake(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "stocktakes"] });
      qc.invalidateQueries({ queryKey: ["inventory", "substitutions"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock"] });
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
      qc.invalidateQueries({ queryKey: ["inventory", "last-prices"] });
      // Както доставка/изписване/прехвърляне: без това хронологията показва
      // за миг кешираните стари редове, преди свежите да пристигнат.
      qc.invalidateQueries({ queryKey: ["inventory", "stock-history"] });
    },
  });
};

export const useSubstitutions = (params: DeliveriesListParams | null) =>
  useQuery({
    queryKey: ["inventory", "substitutions", params],
    queryFn: () => getSubstitutions(params as DeliveriesListParams),
    enabled: !!params,
  });

// Справка „Изтрити дейности" — soft-deleted движения (периодът е по deletedAt).
export const useDeletedMovements = (params: DeliveriesListParams | null) =>
  useQuery({
    queryKey: ["inventory", "deleted", params],
    queryFn: () => getDeletedMovements(params as DeliveriesListParams),
    enabled: !!params,
  });

// Възстановяване на изтрито движение (количествата се прилагат наново).
export const useRestoreDeletedMovement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => restoreDeletedMovement(id),
    onSuccess: () => invalidateAll(qc),
  });
};

// ─── Разнасяне на отчети (report posting) ───────────────────────────────────
export const usePendingReports = (enabled = true) =>
  useQuery({
    queryKey: ["inventory", "pending-reports"],
    queryFn: getPendingReports,
    enabled,
  });

export const useReportPostingPreview = (reportId: number | null) =>
  useQuery({
    queryKey: ["inventory", "report-posting-preview", reportId],
    queryFn: () => getReportPostingPreview(reportId as number),
    enabled: !!reportId,
  });

export const useCreateReportPosting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReportPostingPayload) => createReportPosting(payload),
    onSuccess: () => invalidateAll(qc),
  });
};

// ─── Справки: продадени продукти / рецепти ──────────────────────────────────
export const useSoldProducts = (params: SoldParams | null) =>
  useQuery({
    queryKey: ["inventory", "sold-products", params],
    queryFn: () => getSoldProducts(params as SoldParams),
    enabled: !!params,
  });

// Разбивка по дни за един продаваем (модалът в „Продадени продукти/рецепти").
export const useSoldDaily = (
  kind: "product" | "recipe",
  sellableId: number | null,
  params: SoldParams,
) =>
  useQuery({
    queryKey: ["inventory", "sold-daily", kind, sellableId, params],
    queryFn: () => getSoldDaily(kind, sellableId as number, params),
    enabled: sellableId != null,
  });

export const useSoldRecipes = (params: SoldParams | null) =>
  useQuery({
    queryKey: ["inventory", "sold-recipes", params],
    queryFn: () => getSoldRecipes(params as SoldParams),
    enabled: !!params,
  });

// ─── Редакция / триене ──────────────────────────────────────────────────
// ЕДИНСТВЕНОТО място за инвалидация след складова мутация — новите hook
// модули (useInventoryDeliveries, useInventoryDrafts…) минават оттук.
export const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["inventory", "deliveries"] });
  qc.invalidateQueries({ queryKey: ["inventory", "writeoffs"] });
  qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
  qc.invalidateQueries({ queryKey: ["inventory", "stocktakes"] });
  qc.invalidateQueries({ queryKey: ["inventory", "substitutions"] });
  qc.invalidateQueries({ queryKey: ["inventory", "report-postings"] });
  qc.invalidateQueries({ queryKey: ["inventory", "report-posting-preview"] });
  qc.invalidateQueries({ queryKey: ["inventory", "report-posting-edit"] });
  qc.invalidateQueries({ queryKey: ["inventory", "pending-reports"] });
  qc.invalidateQueries({ queryKey: ["inventory", "stock"] });
  qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
  qc.invalidateQueries({ queryKey: ["inventory", "stock-history"] });
  qc.invalidateQueries({ queryKey: ["inventory", "last-prices"] });
  qc.invalidateQueries({ queryKey: ["inventory", "deleted"] });
  qc.invalidateQueries({ queryKey: ["inventory", "nav-counts"] });
  qc.invalidateQueries({ queryKey: ["inventory", "payable"] });
  qc.invalidateQueries({ queryKey: ["inventory", "drafts"] });
  qc.invalidateQueries({ queryKey: ["inventory", "uninvoiced-notes"] });
};

export const useUpdateDelivery = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateDeliveryPayload }) =>
      updateDelivery(id, payload),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useDeleteDelivery = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteDelivery(id),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useUpdateWriteoff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateWriteoffPayload }) =>
      updateWriteoff(id, payload),
    onSuccess: () => invalidateAll(qc),
    onError: (error) => refreshStockAfterShortage(qc, error),
  });
};

export const useUpdateStocktake = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateStocktakePayload }) =>
      updateStocktake(id, payload),
    onSuccess: () => invalidateAll(qc),
  });
};

// ─── Взаимозаменяеми продукти ─────────────────────────────────────────────
// Групите взаимозаменяеми са ОБЩИ за всички складове.
export const useInterchangeGroups = () =>
  useQuery({
    queryKey: ["inventory", "interchange-groups"],
    queryFn: () => getInterchangeGroups(),
    staleTime: 60 * 1000,
  });

export const useCreateInterchangeGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InterchangeGroupPayload) => createInterchangeGroup(payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["inventory", "interchange-groups"] }),
  });
};

export const useUpdateInterchangeGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: InterchangeGroupPayload }) =>
      updateInterchangeGroup(id, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["inventory", "interchange-groups"] }),
  });
};

export const useDeleteInterchangeGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteInterchangeGroup(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["inventory", "interchange-groups"] }),
  });
};

export const useCreateDelivery = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDeliveryPayload) => createDelivery(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "deliveries"] });
      qc.invalidateQueries({ queryKey: ["inventory", "stock"] });
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
      qc.invalidateQueries({ queryKey: ["inventory", "last-prices"] });
    },
  });
};
